import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API_BASE } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Package, HardDrives, Desktop, WifiHigh, Printer, DeviceMobile,
  FilePdf, Warning, CheckCircle, Ticket, User, Phone, MapPin, Buildings, FilePlus, Plus
} from "@phosphor-icons/react";

const TYPE_LABEL = {
  pc: "PC", serveur: "Serveur", nas: "NAS", switch: "Switch",
  routeur: "Routeur", imprimante: "Imprimante", telephone: "Téléphone",
  onduleur: "Onduleur", autre: "Autre",
};
const TYPE_ICONS = {
  pc: Desktop, serveur: HardDrives, nas: HardDrives, switch: WifiHigh,
  routeur: WifiHigh, imprimante: Printer, telephone: DeviceMobile,
  onduleur: Package, autre: Package,
};

export default function MesServices() {
  const { user } = useAuth();
  const params = useParams();
  const clientId = params.id;                     // admin route: /clients/:id/services
  const isSelfView = !clientId;                    // client route: /mes-services
  const isStaff = user?.role === "admin" || user?.role === "technicien";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const url = isSelfView ? "/me/subscriptions" : `/clients/${clientId}/subscriptions`;
      const r = await api.get(url);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de charger les abonnements");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const downloadInventory = async () => {
    const id = clientId || user?.id;
    try {
      const r = await api.get(`/clients/${id}/inventory.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur PDF"); }
  };

  const downloadLabels = async () => {
    try {
      const r = await api.get(`/inventory/labels.pdf`, { params: { client_id: clientId || user?.id }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur PDF"); }
  };

  if (loading || !data) return <div className="text-muted-foreground">Chargement…</div>;

  const { client, contracts, equipment, equipment_by_type, stats } = data;

  return (
    <div className="space-y-6">
      <div>
        {isStaff && !isSelfView && (
          <Link to="/utilisateurs" className="text-sm text-muted-foreground flex items-center gap-1" data-testid="back-users">
            <ArrowLeft size={16}/> Utilisateurs
          </Link>
        )}
        <div className="label-caps mt-2">Abonnements &amp; services</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">
          {isSelfView ? "Mes services" : client.full_name}
        </h1>
        {!isSelfView && (
          <p className="text-sm text-muted-foreground mt-1">Vue synthèse des contrats et équipements souscrits par ce client.</p>
        )}
      </div>

      {/* Client info card */}
      <Card data-testid="client-info-card">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <InfoTile icon={User} label="Contact" value={client.full_name}/>
            <InfoTile icon={Buildings} label="Société" value={client.company || "—"}/>
            <InfoTile icon={Phone} label="Téléphone fixe" value={client.phone || "—"}/>
            <InfoTile icon={DeviceMobile} label="Portable" value={client.mobile_phone || "—"}/>
            <InfoTile icon={MapPin} label="Adresse" value={client.address || "—"}/>
            <InfoTile icon={MapPin} label="Code postal" value={client.postal_code || "—"}/>
            <InfoTile icon={MapPin} label="Ville" value={client.city || "—"}/>
            <InfoTile icon={MapPin} label="Pays" value={client.country || "—"}/>
          </div>
          {client.notes && (
            <div className="mt-4 pt-4 border-t">
              <div className="text-[0.7rem] uppercase tracking-wider text-muted-foreground mb-1.5">Notes internes</div>
              <p className="text-sm whitespace-pre-wrap">{client.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Contrats actifs" value={stats.contracts_count} accent="emerald"/>
        <StatTile label="Équipements gérés" value={stats.equipment_count} accent="blue"/>
        <StatTile label="Tickets ouverts" value={stats.open_tickets} accent="orange"/>
        <StatTile label="Total tickets" value={stats.total_tickets} accent="slate"/>
      </div>

      {/* Contracts */}
      <section>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="font-display text-xl font-semibold">Contrats de maintenance</h2>
          <div className="flex items-center gap-2">
            <Badge variant="outline">{contracts.length}</Badge>
            {isStaff && !isSelfView && (
              <Link to={`/contrats?new=1&client_id=${clientId}`}>
                <Button size="sm" variant="secondary" data-testid="add-contract-for-client">
                  <FilePlus size={14} className="mr-1.5"/> Ajouter un contrat
                </Button>
              </Link>
            )}
          </div>
        </div>
        {contracts.length === 0 ? (
          <p className="text-muted-foreground text-sm">Aucun contrat actif.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {contracts.map((c) => (
              <Card key={c.id} data-testid={`sub-contract-${c.id}`} className="card-hover">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="label-caps">{c.name}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {c.start_date || "—"} → {c.end_date || "—"}
                      </div>
                    </div>
                    <span className={`text-xs font-medium ${c.over_limit ? "text-destructive" : "text-emerald-600"} flex items-center gap-1`}>
                      {c.over_limit ? <><Warning size={12}/> Dépassement</> : <><CheckCircle size={12}/> Sous quota</>}
                    </span>
                  </div>
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Heures consommées</span>
                      <span className="mono font-semibold">{c.hours_consumed}h / {c.monthly_hours}h</span>
                    </div>
                    <Progress value={Math.min(100, c.used_percent)} className="h-2"/>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Tarif</span>
                      <span className="mono">{c.hourly_rate} €/h</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Equipment */}
      <section>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="font-display text-xl font-semibold">Parc informatique</h2>
          <div className="flex items-center gap-2">
            <Badge variant="outline">{equipment.length}</Badge>
            {isStaff && (
              <>
                {!isSelfView && (
                  <Link to={`/inventaire?new=1&client_id=${clientId}`}>
                    <Button size="sm" variant="secondary" data-testid="add-equipment-for-client">
                      <Plus size={14} className="mr-1.5"/> Ajouter équipement
                    </Button>
                  </Link>
                )}
                <Button size="sm" variant="secondary" onClick={downloadInventory} data-testid="download-inventory">
                  <FilePdf size={14} className="mr-1.5"/> Inventaire PDF
                </Button>
                <Button size="sm" variant="secondary" onClick={downloadLabels} data-testid="download-labels">
                  <FilePdf size={14} className="mr-1.5"/> Étiquettes
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Type summary chips */}
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(equipment_by_type).map(([t, n]) => {
            const Icon = TYPE_ICONS[t] || Package;
            return (
              <div key={t} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary" data-testid={`type-${t}`}>
                <Icon size={12} weight="duotone"/>
                <span className="font-medium">{TYPE_LABEL[t] || t}</span>
                <span className="mono opacity-70">×{n}</span>
              </div>
            );
          })}
        </div>

        {equipment.length === 0 ? (
          <p className="text-muted-foreground text-sm">Aucun équipement enregistré.</p>
        ) : (
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground border-b">
                  <tr>
                    <th className="text-left py-3 px-4">Type</th>
                    <th className="text-left py-3 px-4">Nom</th>
                    <th className="text-left py-3 px-4 hidden md:table-cell">N° série</th>
                    <th className="text-left py-3 px-4 hidden md:table-cell">IP</th>
                    <th className="text-left py-3 px-4">Emplacement</th>
                    <th className="text-left py-3 px-4 hidden lg:table-cell">Garantie</th>
                  </tr>
                </thead>
                <tbody>
                  {equipment.map((e) => {
                    const Icon = TYPE_ICONS[e.type] || Package;
                    return (
                      <tr key={e.id} className="border-b" data-testid={`sub-eq-${e.id}`}>
                        <td className="py-3 px-4">
                          <div className="inline-flex items-center gap-1.5">
                            <Icon size={14} weight="duotone" className="text-primary"/>
                            <span className="text-xs">{TYPE_LABEL[e.type] || e.type}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 font-medium">{e.name}</td>
                        <td className="py-3 px-4 mono text-xs hidden md:table-cell">{e.serial_number || "—"}</td>
                        <td className="py-3 px-4 mono text-xs hidden md:table-cell">{e.ip_address || "—"}</td>
                        <td className="py-3 px-4">{e.location || "—"}</td>
                        <td className="py-3 px-4 text-xs text-muted-foreground hidden lg:table-cell">{e.warranty_end || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </section>

      {/* Ticket CTA */}
      <section>
        <Card>
          <CardContent className="pt-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="label-caps">Support</div>
              <h3 className="font-display text-lg font-semibold mt-1">Besoin d&apos;aide ?</h3>
              <p className="text-sm text-muted-foreground mt-1">Ouvrez un ticket lié à un équipement ou contrat.</p>
            </div>
            <Link to={isSelfView ? "/nouvelle-demande" : `/tickets?client_id=${clientId}`}>
              <Button data-testid="cta-new-ticket"><Ticket size={16} className="mr-2"/> {isSelfView ? "Nouvelle demande" : "Voir tickets"}</Button>
            </Link>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

function InfoTile({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="w-8 h-8 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
        <Icon size={14} weight="duotone"/>
      </div>
      <div className="min-w-0">
        <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="font-medium truncate">{value || "—"}</div>
      </div>
    </div>
  );
}

function StatTile({ label, value, accent }) {
  const accentBg = {
    emerald: "border-l-emerald-500",
    blue: "border-l-blue-500",
    orange: "border-l-orange-500",
    slate: "border-l-slate-500",
  }[accent] || "border-l-slate-500";
  return (
    <Card className={`border-l-4 ${accentBg}`}>
      <CardContent className="pt-5">
        <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="font-display text-2xl font-bold mono mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}
